import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Cookie } from "lucide-react";

import {
  OPEN_PREFERENCES_EVENT,
  getConsent,
  setConsent,
  type Consent,
} from "@/lib/analytics";

export default function CookieConsent() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => getConsent() === null);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_PREFERENCES_EVENT, show);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, show);
  }, []);

  const choose = (consent: Consent) => {
    setConsent(consent);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-text"
          className="fixed z-[70] inset-x-4 bottom-4 md:inset-x-auto md:left-8 md:bottom-8 md:max-w-md"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex flex-col gap-4 rounded-2xl border-2 border-line bg-surface-raised dark:bg-[#121212] p-5 text-ink shadow-[0_8px_24px_rgba(11,27,58,0.12)] dark:border-[#2F2F2F4D] dark:shadow-none">
            <div className="flex items-start gap-3">
              <Cookie
                className="h-5 w-5 shrink-0 mt-0.5 text-brand dark:text-brand-soft"
                aria-hidden="true"
              />
              <div className="flex flex-col gap-1">
                <p id="cookie-consent-title" className="font-medium">
                  {t("cookies.title")}
                </p>
                <p
                  id="cookie-consent-text"
                  className="text-sm font-light text-ink-muted dark:text-[#DADADA]"
                >
                  {t("cookies.text")}
                </p>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => choose("denied")}
                className="cursor-pointer py-2 px-5 rounded-full border border-line text-sm text-ink-muted hover:text-ink transition-colors"
              >
                {t("cookies.reject")}
              </button>
              <button
                type="button"
                onClick={() => choose("granted")}
                className="cursor-pointer py-2 px-5 rounded-full text-sm text-on-brand bg-brand hover:bg-brand-deep transition-colors dark:text-white dark:bg-[#2f2f2f]/60 dark:hover:bg-[#393939]"
              >
                {t("cookies.accept")}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
